class ProductsByCollection {
  constructor(section, maxProducts) {
    this.section = section;

    if (!this.section) return;

    this.maxProducts = maxProducts;

    this.thumbs = this.section.querySelectorAll('[data-thumbs]');
    this.triggers = this.section.querySelectorAll('[data-trigger]');
    this.contents = this.section.querySelectorAll('[data-content]');
    this.classes = {
      activeThumbs: 'products-by-collection__thumbs--active',
      activeTrigger: 'products-by-collection__button--active',
      activeContent: 'products-by-collection__products--active',
      activeThumbWrapper: 'products-by-collection__thumb-wrapper--active'
    };

    this.radius = 0;
    this.draggable = null;
    this.currentRotation = 0;
    this.step = 360 / this.maxProducts;

    onUserInteraction(() => {
      this.init();
      this.initWheel();
    });
  }

  init() {
    this.triggers.forEach(trigger => {
      trigger.addEventListener('click', e => {
        e.preventDefault();

        this.activateTrigger(trigger.dataset.trigger);
      });
    });
  }

  activateTrigger(id) {
    const thumb = this.section.querySelector(`[data-thumbs=${ id }]`);
    const content = this.section.querySelector(`[data-content=${ id }]`);

    this.thumbs.forEach(t => t.classList.remove(this.classes.activeThumbs));
    this.triggers.forEach(t => t.classList.remove(this.classes.activeTrigger));
    this.contents.forEach(c => c.classList.remove(this.classes.activeContent));

    thumb?.classList.add(this.classes.activeThumbs);
    this.triggers.forEach(t => {
      if (t.dataset.trigger === id) t.classList.add(this.classes.activeTrigger);
    });
    content?.classList.add(this.classes.activeContent);

    if (thumb && window.gsap) {
      this.wheel = thumb;
      this.items = thumb.querySelectorAll('.products-by-collection__thumb-wrapper');
      this.slider = this.section.querySelector(`[data-content='${ id }'] .swiper`);
      this.currentRotation = 0;

      this.updateStep();
      this.updateRadius();
      this.centerInitialActive();
      this.rotateItems(this.currentRotation);
      this.initThumbClicks();
      this.initDraggable();
      this.initSlideChange(id);
    }
  }

  initWheel() {
    const scripts = [window.gsapUrl, window.gsapDraggableUrl];

    const setup = () => {
      if (!window?.gsap || !window?.Draggable) return;

      gsap.registerPlugin(Draggable);

      this.wheel = this.section.querySelector('.products-by-collection__thumbs--active');

      if (!this.wheel) return;

      const activeThumbData = this.wheel.dataset.thumbs;

      this.items = this.wheel.querySelectorAll('.products-by-collection__thumb-wrapper');
      this.slider = this.section.querySelector(`[data-content='${ activeThumbData }'] .swiper`);

      this.updateStep();
      this.updateRadius();
      this.centerInitialActive();
      this.rotateItems(this.currentRotation);
      this.initThumbClicks();
      this.initDraggable();
      this.initSlideChange(activeThumbData);
      this.initResize();
    };

    if (!window.gsap || !window.Draggable) {
      isIntersecting(this.section, () => {
        const fragment = document.createDocumentFragment();

        scripts.forEach(url => {
          const script = document.createElement('script');

          script.src = url;

          fragment.appendChild(script);
        });

        document.body.appendChild(fragment);

        waitForElemObject('gsap', setup);
        waitForElemObject('Draggable', setup);
      });
    } else {
      setup();
    }
  }

  updateStep() {
    if (!this.items.length) return;

    this.step = 360 / this.items.length;
  }

  updateRadius() {
    if (!this.wheel || !this.items.length) return;

    const wheelRect = this.wheel.getBoundingClientRect();
    const itemRect = this.items[0].getBoundingClientRect();

    const wheelRadius = Math.min(wheelRect.width, wheelRect.height) / 2;
    const itemRadius = Math.max(itemRect.width, itemRect.height) / 2;

    this.radius = wheelRadius - itemRadius;
  }

  centerInitialActive() {
    if (!this.items) return;

    const activeItemIndex = [...this.items].findIndex(item =>
      item.classList.contains(this.classes.activeThumbWrapper)
    );

    if (activeItemIndex === -1) return;

    const baseAngle = this.baseAngle;
    this.currentRotation = baseAngle - this.step * activeItemIndex;
  }

  centerActive(index) {
    const baseAngle = this.baseAngle;
    const target = baseAngle - this.step * index;
    const current = this.currentRotation;

    let delta = ((target - current + 540) % 360) - 180;
    const finalRotation = current + delta;

    const obj = { val: current };

    gsap.to(obj, {
      val: finalRotation,
      duration: 0.5,
      ease: 'power2.out',
      onUpdate: () => {
        this.currentRotation = obj.val;
        this.rotateItems(this.currentRotation);
      }
    });
  }

  rotateItems(rotation) {
    if (!this.items) return;

    this.items.forEach((item, i) => {
      const angle = this.step * i + rotation;
      const img = item.querySelector('img');

      gsap.set(item, {
        rotation: angle,
        x: Math.cos(angle * Math.PI / 180) * this.radius,
        y: Math.sin(angle * Math.PI / 180) * this.radius,
        xPercent: -50,
        yPercent: -50
      });

      if (img) gsap.set(img, { rotation: -angle });
    });
  }

  initThumbClicks() {
    if (!this.items || !this.slider) return;

    const onTap = (e, i, item) => {
      e.preventDefault();

      this.items.forEach(it => it.classList.remove(this.classes.activeThumbWrapper));
      item.classList.add(this.classes.activeThumbWrapper);

      this.slider.swiper.slideTo(i);
      setTimeout(() => this.centerActive(activeIndex), this.slider.swiper.params.speed);
    };

    this.items.forEach((item, i) => {
      item.addEventListener('click', e => onTap(e, i, item));
      item.addEventListener('touchend', e => onTap(e, i, item));
    });
  }

  initDraggable() {
    if (!this.wheel) return;

    if (this.draggable) {
      if (Array.isArray(this.draggable)) {
        this.draggable[0].kill();
      } else {
        this.draggable.kill();
      }

      this.draggable = null;
    }

    const _this = this;
    const proxy = document.createElement('div');

    this.draggable = Draggable.create(proxy, {
      type: 'x,y',
      inertia: true,
      trigger: this.wheel,

      onDrag() {
        const delta = this.deltaX + this.deltaY;

        _this.currentRotation += delta * 0.1;
        _this.rotateItems(_this.currentRotation);
      },

      onThrowUpdate() {
        const delta = this.deltaX + this.deltaY;

        _this.currentRotation += delta * 0.1;
        _this.rotateItems(_this.currentRotation);
      },

      onRelease() {
        const snapped = Math.round(_this.currentRotation / _this.step) * _this.step;

        gsap.to({ val: _this.currentRotation }, {
          val: snapped,
          duration: 0.4,
          onUpdate() {
            _this.currentRotation = this.targets()[0].val;
            _this.rotateItems(_this.currentRotation);
          }
        });
      }
    });
  }

  initSlideChange(id) {
    this.sliderEvents = this.sliderEvents || {};

    if (!this.slider || this.sliderEvents[id]) return;

    if (!this.slider.swiper) {
      waitForElemObject('swiper', () => this.initSlideChange(id), this.slider);

      return;
    }

    this.sliderEvents[id] = true;
    this.slider.swiper?.on('slideChange', swiper => {
      const activeIndex = swiper.realIndex;
      const nextSlideVideo = swiper.slides[activeIndex].querySelector('video');
      const prevSlideVideo = swiper.slides[swiper.previousIndex]?.querySelector('video');

      this.items.forEach(i => i.classList.remove(this.classes.activeThumbWrapper));
      this.items[activeIndex].classList.add(this.classes.activeThumbWrapper);

      if (prevSlideVideo && !prevSlideVideo.paused) {
        prevSlideVideo.click();
      }

      setTimeout(() => {
        this.centerActive(activeIndex);

        setTimeout(() => {
          if (nextSlideVideo && nextSlideVideo.paused) {
            nextSlideVideo.click();
          }
        }, 100);
      }, swiper.params.speed);
    });
  }

  initResize() {
    window.addEventListener('resize', () => {
      this.updateRadius();
      this.centerInitialActive();
      this.rotateItems(this.currentRotation);
    });
  }

  get baseAngle() {
    return window.innerWidth >= 1440 ? 0 : -90;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const sections = document.querySelectorAll('section[data-products-by-collection]');

  sections.forEach(section => {
    const maxProducts = parseInt(section.dataset.maxProducts, 10) || 18;

    isIntersecting(section, () => new ProductsByCollection(section, maxProducts));
  });
});
